import { useNavigate } from 'react-router-dom';
import {
  Clock,
  KeyRound,
  Link2,
  Mail,
  Phone,
  UserPen,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { RoleCode } from '@tracker/shared';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';

// ── Helpers ────────────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<RoleCode, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  TEAM_LEADER: 'Team Leader',
  USER: 'Employee',
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso + (iso.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
}

// ── Glassmorphism card wrapper ─────────────────────────────────────────────────

function GlassCard({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn(
      'flex h-full flex-col overflow-hidden rounded-2xl border border-border/60 dark:border-border/50',
      'bg-card/90 dark:bg-card shadow-sm backdrop-blur-sm',
      className,
    )}>
      {children}
    </div>
  );
}

// ── 1. Profile Hero ───────────────────────────────────────────────────────────

interface ProfileHeroProps {
  user: ReturnType<typeof useAuth>['user'];
  profile: ReturnType<typeof useAuth>['profile'];
  photo_url: string | null;
  roles: RoleCode[];
  navigate: ReturnType<typeof useNavigate>;
}

function ProfileHeroCard({ user, profile, photo_url, roles, navigate }: ProfileHeroProps) {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || '—';
  const initials = (() => {
    const f = user?.first_name?.trim()[0] ?? '';
    const l = user?.last_name?.trim()[0] ?? '';
    return (f + l).toUpperCase() || '?';
  })();
  const avatarUrl = photo_url ?? profile?.avatar_url ?? null;

  const contact: { icon: LucideIcon; text: string }[] = [];
  if (user?.email) contact.push({ icon: Mail, text: user.email });
  if (user?.phone) contact.push({ icon: Phone, text: user.phone });
  if (user?.last_login_at) contact.push({ icon: Clock, text: `Last login ${timeAgo(user.last_login_at)}` });

  return (
    <GlassCard>
      <div className="h-1 bg-gradient-to-r from-teal-500 via-teal-400 to-emerald-400" />

      <div className="p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl border-2 border-white shadow-sm dark:border-border">
            {avatarUrl ? (
              <img src={avatarUrl} alt={fullName} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-teal-600 text-lg font-bold text-white">
                {initials}
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h2 className="truncate text-lg font-bold leading-tight text-slate-800 dark:text-slate-100">{fullName}</h2>
              {user?.employee_code && (
                <span className="text-xs text-slate-400 dark:text-slate-500">#{user.employee_code}</span>
              )}
              <Badge variant={user?.is_active ? 'default' : 'secondary'} className="text-[10px]">
                {user?.is_active ? 'Active' : 'Inactive'}
              </Badge>
              {roles.map((r) => (
                <Badge key={r} variant="outline" className="text-[10px] font-medium">
                  {ROLE_LABELS[r] ?? r}
                </Badge>
              ))}
            </div>

          </div>

          <Button size="sm" variant="outline" className="shrink-0" onClick={() => navigate('/account/manage')}>
            <UserPen className="h-3.5 w-3.5" />
            Edit Profile
          </Button>
        </div>

        {contact.length > 0 && (
          <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1.5 border-t border-border/40 pt-3 sm:grid-cols-2 xl:grid-cols-4">
            {contact.map(({ icon: Icon, text }) => (
              <span key={text} className="flex min-w-0 items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <span className="truncate">{text}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </GlassCard>
  );
}

// ── 2. Quick Links ────────────────────────────────────────────────────────────

interface QuickLink { label: string; icon: React.ElementType; path: string; color: string }

const QUICK_LINKS: QuickLink[] = [
  { label: 'Edit Profile',    icon: UserPen,  path: '/account/manage',   color: 'text-violet-600 bg-violet-50 dark:bg-violet-900/30 dark:text-violet-400' },
  { label: 'Change Password', icon: KeyRound, path: '/account/password', color: 'text-rose-600 bg-rose-50 dark:bg-rose-900/30 dark:text-rose-400' },
];

function QuickLinksCard({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
  return (
    <GlassCard>
      <SectionHeader icon={<Link2 />} title="Quick Links" />
      <div className="flex-1 p-3 sm:p-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
          {QUICK_LINKS.map((link) => {
            const Icon = link.icon;
            return (
              <button
                key={link.path}
                type="button"
                onClick={() => navigate(link.path)}
                className="flex min-w-0 items-center gap-2 rounded-lg border border-border/40 bg-muted/30 px-2.5 py-2 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary active:bg-muted/80"
              >
                <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-md', link.color)}>
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className="truncate text-xs font-medium text-slate-700 dark:text-slate-200">{link.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </GlassCard>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function AccountPage() {
  const { user, profile, photo_url, roles } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="space-y-3 py-2">
      <ProfileHeroCard
        user={user}
        profile={profile}
        photo_url={photo_url}
        roles={roles}
        navigate={navigate}
      />

      <QuickLinksCard navigate={navigate} />
    </div>
  );
}
